import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import moment from "moment";
import _ from "lodash";
import { useAlert } from "react-alert";

import { AuthFormLogin } from "pages/HomePage/components/AuthFormLogin";
import { AuthFormVerification } from "pages/HomePage/components/AuthFormVerification"
import { AuthFormConfirm } from "pages/HomePage/components/AuthFormConfirm";
import { consumerVerification, consumerEnableMFA } from "../../../../lib/api/auth";
import { capitalizedLastName } from "../../../../lib/helpers/formatters";
import { LoadingSpinner } from "../../../../components/LoadingSpinner";
import { getConsumerDataFromUrl } from "../../../../lib/api/common";
import { getLoginByUrl } from "../../../../lib/api/common";
import { AuthFormZip } from "pages/HomePage/components/AuthFormZip";
import { AuthFormAccessCode } from "pages/HomePage/components/AuthFormAccessCode";
import { consumerLogin } from "../../../../lib/api/consumer";

import { AuthForm2FA } from "pages/HomePage/components/AuthForm2FA";

import { MultitenancyLogoPicker } from "multitenancy/multitenancyLogoPicker";

import {
  LoginSuccessfulAccessCode,
  LoginSuccessfulGoogleAccount,
  LoginSuccessful3PointVerification,
  wtError
} from "lib/helpers/dataLayers";

import { unformattedZipCode } from "lib/helpers/formatters";

import { useTranslation } from 'react-i18next';
import { getTenant } from "multitenancy/multitenancyGetTenant";

import "./style.scss";

// Get query for setting consumer data in form
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export const AuthForm = () => {
  const alert = useAlert();
  const [form, setForm] = useState({
    ssn: "",
    last_name: "",
    conditions_agreement: "True",
    dob: "",
    zip: "",
  });

  const [month, setMonth] = useState("00");
  const [day, setDay] = useState("00");
  const [year, setYear] = useState("0000");

  const [userEmail, setUserEmail] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [confirmForm, setConfirmForm] = useState({
    verification_code: ""
  });
  const [initial_login, setInitialLogin] = useState(false);
  const [googleAuthToken, setGoogleAuthToken] = useState("");
  const [currentStep, setCurrentStep] = useState("1");
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [tab, setTab] = useState(getTenant() === "consuegra" ? true : false);
  const [loading, setLoading] = useState(false);

  const [onboardingInfo, setOnboardingInfo] = useState();

  const [disableLogin_3_sec, setDisableLogin_3_sec] = useState(false);

  const [disableGoogleLogin, setDisableGoogleLogin] = useState(false);

  let query = useQuery();
  const id = query.get("id");
  const login_id = query.get("login_id");

  const { t } = useTranslation();

  useEffect(() => {
    const getConsumerData = async () => {
      const response = await getConsumerDataFromUrl(id);

      if (response.success === "True") {
        setForm({
          ssn: "",
          last_name: response.last_name,
          account_number: response.account_number,
          file_number: response.file_number
        });
        const responseYear = moment(response.date_of_birth).format("YYYY");
        const responseMonth = moment(response.date_of_birth).format("MM");
        const responseDay = moment(response.date_of_birth).format("DD");

        setMonth(responseMonth);
        setDay(responseDay);
        setYear(responseYear);
      } else {
        alert.error(`Error: ${response.message}`);
        wtError();
      }
    };

    const makeLoginByUrl = async () => {

      const request = {
        login_id: login_id,
      }

      const response = await getLoginByUrl(request);

      if (response.success === "True") { 
        SuccessLoginGoToCabinet(response)
      } else {
        alert.error(`Error: ${response.message}`);
        wtError();

        setTimeout(() => {
          window.location.replace("/");
        }, 5000);
      }
    }

    if (login_id) {
      makeLoginByUrl();
    }

    if (id) {
      getConsumerData();
    }
    // eslint-disable-next-line
  }, [id]);

  const handleSetCurrentStep = (step) => {
    setCurrentStep(step);
  };

  const handleLogin = async () => {
    setHasError(false);
    setIsButtonDisabled(false);
    setErrorMessage("");

    // Check for empty fields
    if (!form.ssn || !form.last_name) {
      setErrorMessage("Some fields are empty.");
      setHasError(true);
      return;
    }

    setIsButtonDisabled(true);

    const requestForm =
      currentStep === "zip" ? { ...form } : { ..._.omit(form, ["zip"]) };

    if (tab) {
      delete requestForm.dob;
    } else {
      delete requestForm.account_number;
    }

    if (!requestForm.dob && !requestForm.account_number) {
      delete requestForm.dob;
      delete requestForm.account_number;
    } else if (!requestForm.account_number) {
      delete requestForm.account_number;
    } else if (!requestForm.dob) {
      delete requestForm.dob;
    }

    // TODO: This should be updated with redux
    setLoading(true);
    const data = await consumerLogin(requestForm);

    setInitialLogin(data.initial_login);

    setOnboardingInfo(data.onboarding);

    if (data.success === "True") {
      localStorage.setItem(
        "userData",
        JSON.stringify({
          token: data.token,
        })
      );
      localStorage.setItem("user_id", data.user_id);
      if (data.zip_required) {
        setCurrentStep("zip");
      } else {
        if (data.mfa_required === null) {
          setUserEmail(data.email);
          setUserPhone(data.cell_phone);
          setCurrentStep("enable2Fa");
        } else if (data.mfa_required === false) {
          LoginSuccessful3PointVerification(data);
          zeroConsumerVerification();
          return;
        } else {
          setUserEmail(data.email);
          setUserPhone(data.cell_phone);
          setCurrentStep("2");
        }
      }
      setHasError(false);
      setLoading(false);
      setIsButtonDisabled(false);
    } else {
      setHasError(true);
      setIsButtonDisabled(false);
      setErrorMessage(data.message);
      wtError();
      setDisableLogin_3_sec(true);
    }
    setLoading(false);
  };

  const enable2FA = async () => {
    const prepareReqBody = {
      enable_mfa: "True"
    }
    setLoading(true);
    const data = await consumerEnableMFA(prepareReqBody);

    if (data.success === "True") {
      setLoading(false);
      setCurrentStep("2");
    }
  }

  const skip2FA = async () => {
    // skip2FADataLayer();
    const prepareReqBody = {
      enable_mfa: "False"
    }
    setLoading(true);
    const data = await consumerEnableMFA(prepareReqBody);

    if (data.success === "True") {
      setLoading(false);
      LoginSuccessful3PointVerification(data);
      zeroConsumerVerification();
    }
  }

  const zeroConsumerVerification = async () => {
    setLoading(true);
    const data = await consumerVerification({login_flow: true});
    
    if (data.success === "True" && data.message === "Verification Completed") {
      // window.dataLayer = window.dataLayer || [];
      // window.dataLayer.push({
      //   event: "loginSuccessful",
      //   userID: data.user_id,
      // });
      if (data.onboarding) {
        const userId = localStorage.getItem("user_id");
        window.location.replace(`/onboarding?${userId}`);
        // window.location.replace("/onboarding");
      } else {
        const userId = localStorage.getItem("user_id");
        window.location.replace(`/cabinet-active?${userId}`);
        // window.location.replace("/cabinet-active");
      }
    } else {
      setLoading(false);
      setErrorMessage("Invalid verification code.");
      wtError();
      setHasError(true);
    }
  }

  const goToAccessCodeStep = () => {
    setCurrentStep("access_code");
    setDisableLogin_3_sec(false);
  }

  const handleLoginWithAccessCode = async() => {
    const requestForm = {
      access_code: form.access_code
    }
    setLoading(true);
    const data = await consumerLogin(requestForm);

    setInitialLogin(data.initial_login);

    if (data.success === "True") {
      LoginSuccessfulAccessCode(data);
      SuccessLoginGoToCabinet(data);
    } else {
      setLoading(false);
      alert.error(data.message);
      wtError();
      setDisableLogin_3_sec(true);
    }
  }

  const handleGoogleLogin = async (response) => {

    if (response.details === "Cookies are not enabled in current environment.") {
      setDisableGoogleLogin(true);
      return
    }

    if (response.error === "popup_closed_by_user") {
      return
    }

    setGoogleAuthToken(response.tokenId);

    const prepareSendObject = {
      google_auth_token: response.tokenId,
      conditions_agreement: true
    }

    setLoading(true);
    const data = await consumerLogin(prepareSendObject);

    setInitialLogin(data.initial_login);

    if (data.success === "True") {
      LoginSuccessfulGoogleAccount(data)
      SuccessLoginGoToCabinet(data);
    } else {
      setLoading(false);
      setCurrentStep("confirm_SSN");
    }

  }

  const handleConfirmWithGoogleLogin = async () => {
    // Check for empty fields
    setHasError(false);
    if (!form.ssn || !form.last_name) {
      setErrorMessage("Some fields are empty.");
      setHasError(true);
      return;
    }

    const requestForm =
    currentStep === "zip" ? { ...form } : { ..._.omit(form, ["zip"]) };

    if (tab) {
      delete requestForm.dob;
    } else {
      delete requestForm.account_number;
    }

    if (!requestForm.dob && !requestForm.account_number) {
      delete requestForm.dob;
      delete requestForm.account_number;
    } else if (!requestForm.account_number) {
      delete requestForm.account_number;
    } else if (!requestForm.dob) {
      delete requestForm.dob;
    }

    requestForm.google_auth_token = googleAuthToken;

    setLoading(true);
    const data = await consumerLogin(requestForm);

    setInitialLogin(data.initial_login);

    if (data.success === "True") {
      if (data.zip_required) {
        setLoading(false);
        setCurrentStep("zip");
      } else {
        LoginSuccessful3PointVerification(data);
        SuccessLoginGoToCabinet(data);
      }

    } else if (data.success === "False") {
      setLoading(false);
      alert.error(data.message);
      wtError();
    }
  }
  
  const SuccessLoginGoToCabinet = (data) => {

    localStorage.setItem(
      "userData",
      JSON.stringify({
        token: data.token,
        // TODO need to change from data.role after back fix
        role: "user",
        verified: data.verified
      })
    );
    localStorage.setItem("firstLogin", true);
    localStorage.setItem("user_id", data.user_id);

    // window.dataLayer = window.dataLayer || [];
    // window.dataLayer.push({
    //   event: "loginSuccessful",
    //   userID: data.user_id,
    // });
    if (data.onboarding) {
      const userId = localStorage.getItem("user_id");
      window.location.replace(`/onboarding?${userId}`);
      // window.location.replace("/onboarding");
    } else {
      const userId = localStorage.getItem("user_id");
      window.location.replace(`/cabinet-active?${userId}`);
      // window.location.replace("/cabinet-active");
    }
  }

  const handleConfirm = async () => {
    setIsButtonDisabled(true);
    setLoading(true);

    // TODO: This shopuld be updated with redux
    const data = await consumerVerification({ ...confirmForm, login_flow: true });
    if (data.success === "True" && data.message === "Verification Completed") {
      LoginSuccessful3PointVerification(data);
      // window.dataLayer = window.dataLayer || [];
      // window.dataLayer.push({
      //   event: "loginSuccessful",
      //   userID: data.user_id,
      // });
      if (onboardingInfo) {
        const userId = localStorage.getItem("user_id");
        window.location.replace(`/onboarding?${userId}`);
        // window.location.replace("/onboarding");
      } else {
        const userId = localStorage.getItem("user_id");
        window.location.replace(`/cabinet-active?${userId}`);
        // window.location.replace("/cabinet-active");
      }
    } else {
      wtError();
      setErrorMessage("Invalid verification code.");
      setLoading(false);
      setHasError(true);
      setIsButtonDisabled(false);
    }
  };

  const handleChange = (e) => {
    if (e.target.name === "last_name") {
      const name = e.target.value.trim().toLowerCase();
      setForm({
        ...form,
        [e.target.name]: capitalizedLastName(name),
      });
    } else if (e.target.name === "ssn") {
      if (e.target.value.length <= 4) {
        setForm({ ...form, [e.target.name]: e.target.value });
        return;
      } else {
        return;
      }
    } else if (e.target.name === "zip") {
      if (
        (e.target.value.length <= 10) ||
        e.target.value === ""
      ) {
        setForm({ ...form, [e.target.name]: unformattedZipCode(e.target.value) });
        return;
      } else {
        return;
      }
    } else {
      setForm({ ...form, [e.target.name]: e.target.value });
    }
  };

  const handleAccountNumberChange = (e) => {
    if (e.target.value.length > 4) {
      return;
    } else {
      setForm({ ...form, account_number: e.target.value });
    }
  };

  const handleFiletNumberChange = (e) => {
    setForm({ ...form, file_number: e.target.value });
  };

  const handleChangeMonthsSelect = (event) => {
    setMonth(event.target.value);
    setForm({ ...form, dob: `${year}-${event.target.value}-${day}` });
  };

  const handleChangeDaysSelect = (event) => {
    setDay(event.target.value);
    setForm({ ...form, dob: `${year}-${month}-${event.target.value}` });
  };
  const handleChangeYearsSelect = (event) => {
    setYear(event.target.value);
    setForm({ ...form, dob: `${event.target.value}-${month}-${day}` });
  };

  const handleConfirmChange = (e) => {
    if (userEmail.length > 0) {
      setConfirmForm({
        ...confirmForm,
        email: userEmail,
        [e.target.name]: e.target.value,
      });
    } else if (userPhone.length > 0) {
      setConfirmForm({
        ...confirmForm,
        phone_number: userPhone,
        [e.target.name]: e.target.value,
      });
    }
  };

  const handleGoBack = () => {
    setDisableLogin_3_sec(false);
    setErrorMessage("");
    setHasError(false);
    if (form.zip && form.zip.length > 0 && currentStep === "2") {
      setCurrentStep("zip");
      return;
    }

    if (currentStep === "zip") {
      setCurrentStep("1");
      setForm({ ...form, zip: "" });
      return;
    }

    if (currentStep === "confirm_SSN") {
      setCurrentStep("1");
      return;
    }

    if (currentStep === "access_code") {
      setCurrentStep("1");
      return;
    }

    const stepBack = currentStep - 1;
    setCurrentStep(stepBack.toString());
  };

  return (
    <>
      {login_id ? <LoadingSpinner overlayType="overlay" middleFixed={true}/> : (
        <div className="auth-form">
          <div className="logo-container">
            <MultitenancyLogoPicker />
          </div>
          {currentStep === "1" &&
            (loading ? (
              <LoadingSpinner overlayType="overlay" middleFixed={true}/>
            ) : (
              <AuthFormLogin
                confirmSSNStep={false}
                goToAccessCodeStep={goToAccessCodeStep}
                handleConfirmWithGoogleLogin={handleConfirmWithGoogleLogin}
                handleGoBack={handleGoBack}
                handleChange={handleChange}
                handleAccountNumberChange={handleAccountNumberChange}
                handleFiletNumberChange={handleFiletNumberChange}
                handleLogin={handleLogin}
                handleGoogleLogin={handleGoogleLogin}
                defaultSSN={form.ssn}
                defaultLastName={form.last_name}
                defaultAccountNumber={form.account_number}
                defaultFileNumber={form.file_number}
                currentMonth={month}
                currentDay={day}
                currentYear={year}
                handleChangeMonthsSelect={handleChangeMonthsSelect}
                handleChangeDaysSelect={handleChangeDaysSelect}
                handleChangeYearsSelect={handleChangeYearsSelect}
                isButtonDisabled={isButtonDisabled}
                tab={tab}
                setTab={setTab}
                errorMessage={errorMessage}
                hasError={hasError}
                disableLogin_3_sec={disableLogin_3_sec}
                disableGoogleLogin={disableGoogleLogin}
              />
            ))}
          {currentStep === "zip" &&
            (loading ? (
              <LoadingSpinner overlayType="overlay" />
            ) : (
              <AuthFormZip
                defaultZip={form.zip}
                handleGoBack={handleGoBack}
                handleSetCurrentStep={handleSetCurrentStep}
                setLoading={setLoading}
                handleChange={handleChange}
                handleLogin={handleLogin}
                errorMessage={errorMessage}
                hasError={hasError}
              />
            ))}
          {currentStep === "2" &&
            (loading ? (
              <LoadingSpinner overlayType="overlay" />
            ) : (
              <AuthFormVerification
                handleSetCurrentStep={handleSetCurrentStep}
                setUserEmail={setUserEmail}
                setUserPhone={setUserPhone}
                handleGoBack={handleGoBack}
                userPhone={userPhone}
                userEmail={userEmail}
                setErrorMessage={setErrorMessage}
                setHasError={setHasError}
                isButtonDisabled={isButtonDisabled}
                setIsButtonDisabled={setIsButtonDisabled}
                setLoading={setLoading}
                initial_login={initial_login}
              />
            ))}
          {currentStep === "3" &&
            (loading ? (
              <LoadingSpinner overlayType="overlay" />
            ) : (
              <AuthFormConfirm
                handleConfirmChange={handleConfirmChange}
                handleConfirm={handleConfirm}
                handleGoBack={handleGoBack}
                email={userEmail}
                phone={userPhone}
                isButtonDisabled={isButtonDisabled}
                errorMessage={errorMessage}
              />
            ))}
            {currentStep === "confirm_SSN" &&
              (loading ? (
                <LoadingSpinner overlayType="overlay" />
              ) : (
                <AuthFormLogin
                  confirmSSNStep={true}
                  handleConfirmWithGoogleLogin={handleConfirmWithGoogleLogin}
                  handleGoBack={handleGoBack}
                  handleChange={handleChange}
                  handleAccountNumberChange={handleAccountNumberChange}
                  handleFiletNumberChange={handleFiletNumberChange}
                  handleLogin={handleLogin}
                  handleGoogleLogin={handleGoogleLogin}
                  defaultSSN={form.ssn}
                  defaultLastName={form.last_name}
                  defaultAccountNumber={form.account_number}
                  defaultFileNumber={form.file_number}
                  currentMonth={month}
                  currentDay={day}
                  currentYear={year}
                  handleChangeMonthsSelect={handleChangeMonthsSelect}
                  handleChangeDaysSelect={handleChangeDaysSelect}
                  handleChangeYearsSelect={handleChangeYearsSelect}
                  isButtonDisabled={isButtonDisabled}
                  tab={tab}
                  setTab={setTab}
                  errorMessage={errorMessage}
                  hasError={hasError}
                  disableLogin_3_sec={disableLogin_3_sec}
                />
              ))}
            {currentStep === "access_code" &&
              (loading ? (
                <LoadingSpinner overlayType="overlay" />
              ) : (
                <AuthFormAccessCode
                  handleGoBack={handleGoBack}
                  handleLoginWithAccessCode={handleLoginWithAccessCode}
                  handleChange={handleChange}
                  errorMessage={errorMessage}
                  hasError={hasError}
                  disableLogin_3_sec={disableLogin_3_sec}
                />
              ))
            }
            {currentStep === "enable2Fa" &&
              (loading ? (
                <LoadingSpinner overlayType="overlay" />
              ) : (
                <AuthForm2FA
                  enable2FA={enable2FA}
                  skip2FA={skip2FA}
                />
              ))
            }
            <p className="footer-text">
              {t("This communication is from a debt collector and is an attempt to collect a debt. Any information obtained will be used for that purpose.")}
            </p>
        </div>
      )}
    </>
  );
};
